import React from "react";
import type { PublicCommentConfig } from "../types";
import type { UseAgendaCommentsReturn } from "../hooks/useAgendaComments";
import { SearchIcon } from "./icons";
import { countPublicComments } from "../utils";
import AgendaCommentRow from "./AgendaCommentRow";

interface Props {
  config: PublicCommentConfig;
  state: UseAgendaCommentsReturn;
}

const AgendaCommentsList: React.FC<Props> = ({ config, state }) => {
  const { commentsByItem, searchKeyword } = state;

  const totalComments = config.agendaItems.reduce(
    (sum, it) => sum + countPublicComments(commentsByItem[it.id] || []),
    0
  );
  const itemsWithComments = config.agendaItems.filter(
    (it) => countPublicComments(commentsByItem[it.id] || []) > 0
  ).length;

  const q = searchKeyword.toLowerCase().trim();
  const filtered = q
    ? config.agendaItems.filter(
        (it) =>
          it.title.toLowerCase().includes(q) ||
          (it.agenda || "").toLowerCase().includes(q)
      )
    : config.agendaItems;

  const subline = state.loading
    ? "Loading public comments…"
    : `${totalComments} public comment${
        totalComments === 1 ? "" : "s"
      } across ${itemsWithComments} item${
        itemsWithComments === 1 ? "" : "s"
      } · open an item to read and respond.`;

  return (
    <div className="pc-card pc-agenda-card">
      <div className="pc-ag-top">
        <div className="pc-ag-top-left">
          <h2 className="pc-card-title">Agenda items</h2>
          <p className="pc-card-subtitle" aria-live="polite">
            {subline}
          </p>
        </div>
        <div className="pc-search">
          <SearchIcon className="pc-search-icon" />
          <input
            className="pc-input pc-search-input"
            placeholder="Search agenda items…"
            value={searchKeyword}
            onChange={(e) => state.setSearchKeyword(e.target.value)}
            aria-label="Search agenda items"
          />
        </div>
      </div>

      <div className="pc-ag-colhead">
        <span>Agenda</span>
        <span>Title</span>
        <span>Comments</span>
      </div>
      <div className="pc-ag-list">
        {filtered.length === 0 ? (
          <div className="pc-empty pc-empty-pad">
            {q ? (
              <>No agenda items match &ldquo;{searchKeyword}&rdquo;.</>
            ) : (
              "No agenda items available for comment yet."
            )}
          </div>
        ) : (
          filtered.map((item) => (
            <AgendaCommentRow
              key={item.id}
              item={item}
              comments={commentsByItem[item.id] || []}
              modes={config.modes}
              isOpen={state.openItems.includes(item.id)}
              commenterName={state.commenterName}
              characterLimit={config.commentCharacterLimit}
              staffBadgeLabel={config.staffBadgeLabel}
              config={config}
              state={state}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AgendaCommentsList;
